import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Stack, Button, Chip, Grid,
  Table, TableBody, TableCell, TableRow, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  TextField, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  CheckCircle as PaidIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { pdf } from '@react-pdf/renderer';
import VisibleDateField from '../../components/invoice/VisibleDateField';
import InvoiceService from '../../services/InvoiceService';
import InvoicePdfDocument, { InvoicePdfTemplate } from '../../components/invoice/InvoicePdfDocument';

const invoiceService = new InvoiceService();

const statusColors = {
  draft: 'default',
  pending: 'warning',
  paid: 'success',
  overdue: 'error',
  voided: 'default',
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);

const InvoiceDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('professional');
  const [emailBody, setEmailBody] = useState('');
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    const load = async () => {
      try {
        const data = await invoiceService.getById(id);
        setInvoice(data);
      } catch (err) {
        setError(err.message || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const openPaidDialog = () => {
    setPaymentDate(invoice?.paymentDate || new Date().toISOString().slice(0, 10));
    setPaidDialogOpen(true);
  };

  const handleStatusChange = async (newStatus, extra = {}) => {
    setActionLoading(true);
    try {
      const updated = await invoiceService.updateStatus(id, newStatus, extra);
      setInvoice(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await invoiceService.delete(id);
      navigate('/invoice');
    } catch (err) {
      setError(err.message);
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    try {
      const blob = await pdf(<InvoicePdfTemplate invoice={invoice} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber || 'invoice'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Box display="flex" justifyContent="center"><CircularProgress /></Box>
      </Container>
    );
  }

  if (!invoice) {
    return (
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Alert severity="error">{error || 'Invoice not found'}</Alert>
        <Button onClick={() => navigate('/invoice')} sx={{ mt: 2 }}>Back to Invoices</Button>
      </Container>
    );
  }

  const canEdit = invoice.status === 'draft' || invoice.status === 'pending';
  const canDelete = invoice.status === 'draft';
  const canVoid = invoice.status === 'pending' || invoice.status === 'overdue';
  const canMarkPaid = invoice.status === 'pending' || invoice.status === 'overdue';
  const issuer = invoice.issuerSnapshot;
  const org = invoice.organisationSnapshot;

  const generateEmailBody = (template) => {
    const total = formatCurrency(invoice.total);
    const dueDate = new Date(invoice.dueDate).toLocaleDateString('en-AU');
    const issuerName = issuer?.businessName || issuer?.name || 'our business';
    const orgName = org?.name || 'your organisation';
    const bankInfo = issuer?.bankDetails
      ? `\nBank: ${issuer.bankDetails.bankName || ''}\nBSB: ${issuer.bankDetails.bsb || ''}\nAccount: ${issuer.bankDetails.accountNumber || ''}\nAccount Name: ${issuer.bankDetails.accountName || ''}\nReference: ${invoice.invoiceNumber}`
      : '';

    switch (template) {
      case 'professional':
        return `Dear ${org?.contactPerson || orgName},\n\nPlease find attached invoice ${invoice.invoiceNumber} for the amount of ${total}.\n\nInvoice Details:\n- Invoice Number: ${invoice.invoiceNumber}\n- Amount Due: ${total}\n- Due Date: ${dueDate}${bankInfo ? `\n\nPayment Details:${bankInfo}` : ''}\n\nPlease do not hesitate to contact us if you have any questions regarding this invoice.\n\nKind regards,\n${issuerName}`;
      case 'friendly':
        return `Hi ${org?.contactPerson || 'there'},\n\nHope you're doing well! I'm sending through invoice ${invoice.invoiceNumber} for ${total}, due by ${dueDate}.${bankInfo ? `\n\nHere are the payment details:${bankInfo}` : ''}\n\nLet me know if you have any questions!\n\nCheers,\n${issuerName}`;
      case 'brief':
        return `Hi,\n\nInvoice ${invoice.invoiceNumber} — ${total}, due ${dueDate}.${bankInfo ? `\n${bankInfo}` : ''}\n\nThanks,\n${issuerName}`;
      default:
        return '';
    }
  };

  const handleOpenEmailDialog = () => {
    setEmailBody(generateEmailBody(emailTemplate));
    setEmailDialogOpen(true);
  };

  const handleEmailTemplateChange = (_, newTemplate) => {
    if (newTemplate) {
      setEmailTemplate(newTemplate);
      setEmailBody(generateEmailBody(newTemplate));
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/invoice')}>
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={700}>
                {invoice.invoiceNumber}
              </Typography>
              <Chip
                label={invoice.status}
                color={statusColors[invoice.status]}
                size="small"
                sx={{ textTransform: 'capitalize', mt: 0.5 }}
              />
                {invoice.status === 'voided' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This invoice has been voided and can no longer be edited.
                  </Alert>
                )}
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPdf}
            >
              Download PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={handleOpenEmailDialog}
            >
              Generate Email Message
            </Button>
            {canEdit && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/invoice/${id}/edit`)}
              >
                Edit
              </Button>
            )}
            {canMarkPaid && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PaidIcon />}
                disabled={actionLoading}
                onClick={openPaidDialog}
              >
                Mark as Paid
              </Button>
            )}
            {canVoid && (
              <Button
                variant="outlined"
                color="error"
                disabled={actionLoading}
                onClick={() => handleStatusChange('voided')}
              >
                Void Invoice
              </Button>
            )}
            {invoice.status === 'draft' && (
              <Button
                variant="contained"
                disabled={actionLoading}
                onClick={() => handleStatusChange('pending')}
              >
                Issue Invoice
              </Button>
            )}
            {invoice.status === 'pending' && (
              <Button
                variant="outlined"
                color="error"
                disabled={actionLoading}
                onClick={() => handleStatusChange('overdue')}
              >
                Mark Overdue
              </Button>
            )}
            {canDelete && (
              <IconButton color="error" onClick={() => setDeleteOpen(true)}>
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {/* Invoice Details */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>From</Typography>
              <Typography fontWeight={600}>{issuer?.businessName || issuer?.name || '—'}</Typography>
              {issuer?.abn && <Typography variant="body2" color="text.secondary">ABN: {issuer.abn}</Typography>}
              {issuer?.email && <Typography variant="body2">{issuer.email}</Typography>}
              {issuer?.phone && <Typography variant="body2">{issuer.phone}</Typography>}
              {issuer?.address && (
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {[issuer.address.street, issuer.address.city, issuer.address.state, issuer.address.postcode].filter(Boolean).join(', ')}
                </Typography>
              )}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>To</Typography>
              <Typography fontWeight={600}>{org?.name || '—'}</Typography>
              {org?.abn && <Typography variant="body2" color="text.secondary">ABN: {org.abn}</Typography>}
              {org?.email && <Typography variant="body2">{org.email}</Typography>}
              {org?.phone && <Typography variant="body2">{org.phone}</Typography>}
              {org?.contactPerson && <Typography variant="body2">Attn: {org.contactPerson}</Typography>}
              {org?.address && (
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {[org.address.street, org.address.city, org.address.state, org.address.postcode].filter(Boolean).join(', ')}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Dates */}
        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">Issue Date</Typography>
              <Typography fontWeight={600}>{new Date(invoice.issueDate).toLocaleDateString('en-AU')}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">Due Date</Typography>
              <Typography fontWeight={600}>{new Date(invoice.dueDate).toLocaleDateString('en-AU')}</Typography>
            </Grid>
            {invoice.paymentDate && (
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="body2" color="text.secondary">Payment Date</Typography>
                <Typography fontWeight={600}>{new Date(invoice.paymentDate).toLocaleDateString('en-AU')}</Typography>
              </Grid>
            )}
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">Currency</Typography>
              <Typography fontWeight={600}>{invoice.currency}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">Template</Typography>
              <Typography fontWeight={600} sx={{ textTransform: 'capitalize' }}>{invoice.templateId}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Line Items */}
        <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Table>
            <TableBody>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell><Typography fontWeight={600}>Description</Typography></TableCell>
                <TableCell align="center"><Typography fontWeight={600}>Qty</Typography></TableCell>
                <TableCell align="right"><Typography fontWeight={600}>Unit Price</Typography></TableCell>
                <TableCell align="right"><Typography fontWeight={600}>Amount</Typography></TableCell>
              </TableRow>
              {(invoice.lineItems || []).map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                  <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} align="right"><Typography color="text.secondary">Subtotal</Typography></TableCell>
                <TableCell align="right"><Typography fontWeight={600}>{formatCurrency(invoice.subtotal)}</Typography></TableCell>
              </TableRow>
              {invoice.gstIncluded && (
                <TableRow>
                  <TableCell colSpan={3} align="right"><Typography color="text.secondary">GST ({invoice.gstRate}%)</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight={600}>{formatCurrency(invoice.gstAmount)}</Typography></TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={3} align="right"><Typography variant="h6" fontWeight={700}>Total</Typography></TableCell>
                <TableCell align="right"><Typography variant="h6" fontWeight={700}>{formatCurrency(invoice.total)}</Typography></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>

        {/* Notes & Bank Details */}
        {(invoice.notes || issuer?.bankDetails) && (
          <Grid container spacing={3}>
            {invoice.notes && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>Notes</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{invoice.notes}</Typography>
                </Paper>
              </Grid>
            )}
            {issuer?.bankDetails && (
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>Bank Details</Typography>
                  {issuer.bankDetails.bankName && <Typography variant="body2">Bank: {issuer.bankDetails.bankName}</Typography>}
                  {issuer.bankDetails.bsb && <Typography variant="body2">BSB: {issuer.bankDetails.bsb}</Typography>}
                  {issuer.bankDetails.accountNumber && <Typography variant="body2">Account: {issuer.bankDetails.accountNumber}</Typography>}
                  {issuer.bankDetails.accountName && <Typography variant="body2">Name: {issuer.bankDetails.accountName}</Typography>}
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* PDF Preview */}
        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600} mb={2}>PDF Preview</Typography>
          <InvoicePdfDocument invoice={invoice} />
        </Paper>
      </Stack>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Invoice?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete invoice <strong>{invoice.invoiceNumber}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={actionLoading}>
            {actionLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Email Message</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" mb={1}>Email Tone</Typography>
              <ToggleButtonGroup
                value={emailTemplate}
                exclusive
                onChange={handleEmailTemplateChange}
                size="small"
              >
                <ToggleButton value="professional">Professional</ToggleButton>
                <ToggleButton value="friendly">Friendly</ToggleButton>
                <ToggleButton value="brief">Brief</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <TextField
              label="Email Body"
              multiline
              rows={12}
              fullWidth
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => {
                  navigator.clipboard.writeText(emailBody);
                }}
              >
                Copy to Clipboard
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paidDialogOpen} onClose={() => setPaidDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as Paid</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Set the date the payment was received. It defaults to today.
            </Typography>
            <VisibleDateField
              label="Payment Date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaidDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={actionLoading}
            onClick={async () => {
              await handleStatusChange('paid', { paymentDate });
              setPaidDialogOpen(false);
            }}
          >
            {actionLoading ? 'Saving…' : 'Mark as Paid'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InvoiceDetail;
